import type {
  GroupDetailsResponse,
  GroupFile,
  GroupMember,
  GroupPanelWrite,
  GroupSortDirection,
  GroupsListResponse,
  SortableGroupColumn,
} from '@app/application/user-management/types'
import type { JsonFetchOptions } from '@app/utils/types'

import {
  GROUP_PANEL_REQUEST_KEY,
  GROUPS_REQUEST_KEY,
} from '@app/application/user-management/constants'
import { getSignal } from '@app/adapters/abort'
import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'

export const fetchGroups = ({
  q,
  sortBy,
  sortDirection,
  page = 1,
}: {
  q?: string
  sortBy?: SortableGroupColumn
  sortDirection?: GroupSortDirection
  page?: number
} = {}): Promise<GroupsListResponse | null> =>
  SFetch.json<GroupsListResponse>(
    window.Silverfin.Routes.groups_path(window.Silverfin.currentFirmId, {
      format: 'json',
      q,
      sort_by: sortBy,
      sort_direction: sortDirection,
      page,
    }),
    { signal: getSignal(GROUPS_REQUEST_KEY) }
  )

// The panel reads the field errors off a 422 body and keeps itself open, so
// every failure has to reach the caller.
const request = async <R>(
  message: string,
  url: string,
  options?: JsonFetchOptions
): Promise<R | null> => {
  try {
    return await SFetch.json<R>(url, options)
  } catch (error) {
    if (!options?.signal?.aborted) SfLog.error(message, { error })
    throw error
  }
}

export const get = (id: number): Promise<GroupDetailsResponse | null> =>
  request(
    'Cannot load the group',
    window.Silverfin.Routes.group_path(window.Silverfin.currentFirmId, id, {
      format: 'json',
    }),
    { signal: getSignal(GROUP_PANEL_REQUEST_KEY) }
  )

export const create = (
  group: GroupPanelWrite
): Promise<GroupDetailsResponse | null> =>
  request(
    'Cannot create the group',
    window.Silverfin.Routes.groups_path(window.Silverfin.currentFirmId, {
      format: 'json',
    }),
    { method: 'POST', body: { group } }
  )

export const update = (
  id: number,
  group: GroupPanelWrite
): Promise<GroupDetailsResponse | null> =>
  request(
    'Cannot save the group',
    window.Silverfin.Routes.group_path(window.Silverfin.currentFirmId, id, {
      format: 'json',
    }),
    { method: 'PATCH', body: { group } }
  )

export const destroy = (id: number): Promise<null> =>
  request(
    'Cannot delete the group',
    window.Silverfin.Routes.group_path(window.Silverfin.currentFirmId, id, {
      format: 'json',
    }),
    { method: 'DELETE' }
  )

export const pickerUsers = (
  q: string,
  signal?: AbortSignal
): Promise<GroupMember[] | null> =>
  request(
    'Cannot load the staff users',
    window.Silverfin.Routes.picker_users_groups_path(
      window.Silverfin.currentFirmId,
      { format: 'json', q }
    ),
    { signal }
  )

export const pickerCompanies = (
  q: string,
  signal?: AbortSignal
): Promise<GroupFile[] | null> =>
  request(
    'Cannot search client files',
    window.Silverfin.Routes.picker_companies_groups_path(
      window.Silverfin.currentFirmId,
      { format: 'json', q }
    ),
    { signal }
  )
